import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Shield, Target, Zap, Trophy, Users, Brain } from 'lucide-react';
import AuthModal from '../components/AuthModal';
import MatrixRainBackdrop from '../components/MatrixRainBackdrop';
import Footer from '../components/Footer';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState('register');

  const openAuth = (tab) => {
    setAuthTab(tab);
    setShowAuthModal(true);
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      openAuth('register');
    }
  };

  const features = [
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Cyber Defense Training',
      description: 'Hands-on labs for learning defensive security techniques'
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: 'CTF Challenges',
      description: 'Capture the flag competitions to test your skills'
    },
    {
      icon: <Brain className="w-12 h-12" />,
      title: 'AI Threat Detection',
      description: 'Master AI-powered threat hunting and automated defense'
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Real-time Labs',
      description: 'Practice in safe, isolated virtual environments'
    },
    {
      icon: <Trophy className="w-12 h-12" />,
      title: 'Gamified Learning',
      description: 'Earn badges, points, and climb the leaderboard'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Community Driven',
      description: 'Learn from thousands of cybersecurity enthusiasts'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation */}
      <nav
        data-testid="landing-navbar"
        className="relative border-b border-[#00FF00]/20 bg-black/70 backdrop-blur-sm fixed w-full z-50 overflow-hidden"
      >
        {/* Matrix rain + light rays — pointer-events:none, aria-hidden.
            Sits behind the navbar content via z-index layering. */}
        <MatrixRainBackdrop />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-[#00FF00] drop-shadow-[0_0_8px_rgba(0,255,0,0.6)]" />
              <span className="text-xl font-bold drop-shadow-[0_0_6px_rgba(0,0,0,0.9)]">CyberInsti</span>
            </div>
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => openAuth('login')}
                    className="text-gray-100 hover:text-white hover:bg-gray-800/60 backdrop-blur-sm"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={() => openAuth('register')}
                    className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold shadow-[0_0_20px_rgba(0,255,0,0.35)]"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-[#00FF00]/10 border border-[#00FF00]/30 rounded-full">
            <span className="text-[#00FF00] text-sm font-medium">AI-Powered Cybersecurity Training</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Master Cyber Defense
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF00] to-[#00BFFF]">
              Through Practice
            </span>
          </h1>
          <p className="text-xl text-gray-400 mb-8 max-w-3xl mx-auto">
            Learn cybersecurity through guided paths, hands-on challenges, and AI-powered threat detection. 
            From beginner to advanced, build real-world skills in a safe environment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleGetStarted}
              className="bg-[#00FF00] text-black hover:bg-[#00DD00] px-8 py-6 text-lg font-semibold"
            >
              Start Learning Free
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/paths')}
              className="border-[#00BFFF] text-[#00BFFF] hover:bg-[#00BFFF]/10 px-8 py-6 text-lg"
            >
              View Learning Paths
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            <div className="border border-gray-800 rounded-lg p-6 bg-black/30 backdrop-blur-sm">
              <div className="text-3xl font-bold text-[#00FF00] mb-2">500+</div>
              <div className="text-gray-400">Training Rooms</div>
            </div>
            <div className="border border-gray-800 rounded-lg p-6 bg-black/30 backdrop-blur-sm">
              <div className="text-3xl font-bold text-[#00BFFF] mb-2">200K+</div>
              <div className="text-gray-400">Active Users</div>
            </div>
            <div className="border border-gray-800 rounded-lg p-6 bg-black/30 backdrop-blur-sm">
              <div className="text-3xl font-bold text-[#FF9500] mb-2">15+</div>
              <div className="text-gray-400">Learning Paths</div>
            </div>
            <div className="border border-gray-800 rounded-lg p-6 bg-black/30 backdrop-blur-sm">
              <div className="text-3xl font-bold text-[#FF0055] mb-2">24/7</div>
              <div className="text-gray-400">Lab Access</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-black/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">Why Choose CyberInsti?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div 
                key={feature.title}
                className="border border-gray-800 rounded-lg p-8 bg-[#0A0A0A] hover:border-[#00FF00]/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="text-[#00FF00] mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center border border-gray-800 rounded-2xl p-12 bg-gradient-to-br from-[#00FF00]/5 to-[#00BFFF]/5">
          <h2 className="text-4xl font-bold mb-4">Ready to Start Your Cybersecurity Journey?</h2>
          <p className="text-xl text-gray-400 mb-8">
            Join thousands of learners and start building your skills today.
          </p>
          <Button 
            onClick={handleGetStarted}
            className="bg-[#00FF00] text-black hover:bg-[#00DD00] px-8 py-6 text-lg font-semibold"
          >
            Get Started Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)}
          initialTab={authTab}
        />
      )}
    </div>
  );
};

export default Landing;
